import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { identityLabel } from '../../Delivery/Registries/Contracts/facts';
import { DocumentPickerField } from '../documents/DocumentPickerField';
import { useCapabilityLinkMutations, useCapabilityProofs, useProofAssociations } from './hooks';
import { proofKinds } from './proofKinds';
export function AddProofDialog(props) {
    return props.open ? /*#__PURE__*/ _jsx(ProofForm, {
        ...props
    }) : null;
}
function ProofForm({ onOpenChange, capabilityId }) {
    const [document, setDocument] = useState();
    const [kind, setKind] = useState('');
    const [program, setProgram] = useState();
    const [contract, setContract] = useState();
    const [programSearch, setProgramSearch] = useState('');
    const [contractSearch, setContractSearch] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const busy = useRef(false);
    const permission = useCapabilityProofs(capabilityId);
    const allowed = permission.data?.canLink === true && !permission.isError;
    const { addProof } = useCapabilityLinkMutations();
    const { programs, contracts } = useProofAssociations(programSearch, contractSearch);
    const programOptions = (programs.data?.programs ?? []).map((p)=>({
            value: p.id,
            label: p.programNameShort || p.programNameFull
        }));
    const contractOptions = (contracts.data?.contracts ?? []).map((c)=>({
            value: c.id,
            label: identityLabel(c.identity)
        }));
    const close = (next)=>{
        if (!busy.current) onOpenChange(next);
    };
    const submit = async ()=>{
        if (busy.current || !allowed || !document || !kind || !capabilityId) return;
        busy.current = true;
        setError('');
        try {
            await addProof.mutateAsync({
                capabilityId,
                artifactId: document.artifactId,
                proofKind: kind,
                programId: program?.value,
                registryContractId: contract?.value,
                note
            });
            busy.current = false;
            onOpenChange(false);
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not link proof. Try again.'));
        } finally{
            busy.current = false;
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: close,
        size: "md",
        title: "Link document as proof",
        testId: Testids.registryCapabilities.proofDialog,
        footerProps: {
            confirmLabel: 'Link proof',
            loadingLabel: 'Linking…',
            loading: addProof.isPending,
            confirmDisabled: !allowed || !document || !kind || !capabilityId || addProof.isPending,
            onConfirm: ()=>void submit(),
            onCancel: ()=>close(false),
            cancelProps: {
                disabled: addProof.isPending
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-3)]",
            children: [
                permission.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void permission.refetch(),
                    children: "Retry checking proof permission"
                }),
                !permission.isPending && !permission.isError && !allowed && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "You cannot link proof to this capability."
                }),
                /*#__PURE__*/ _jsx(DocumentPickerField, {
                    label: "Document",
                    required: true,
                    disabled: addProof.isPending,
                    value: document,
                    onChange: setDocument
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Kind",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Kind",
                        required: true,
                        value: kind,
                        disabled: addProof.isPending,
                        onChange: (e)=>setKind(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Choose a kind"
                            }),
                            Object.entries(proofKinds).map(([value, label])=>/*#__PURE__*/ _jsx("option", {
                                    value: value,
                                    children: label
                                }, value))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Program (optional)",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Program",
                        value: program?.value,
                        selectedOption: program,
                        options: programOptions,
                        onValueChange: (id)=>setProgram(programOptions.find((p)=>p.value === id)),
                        onSearchValueChange: setProgramSearch,
                        shouldFilter: false,
                        clearable: true,
                        loading: programs.isFetching,
                        disabled: addProof.isPending
                    })
                }),
                programs.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void programs.refetch(),
                    children: "Retry loading programs"
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Contract (optional)",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Contract",
                        value: contract?.value,
                        selectedOption: contract,
                        options: contractOptions,
                        onValueChange: (id)=>setContract(contractOptions.find((c)=>c.value === id)),
                        onSearchValueChange: setContractSearch,
                        shouldFilter: false,
                        clearable: true,
                        loading: contracts.isFetching,
                        disabled: addProof.isPending
                    })
                }),
                contracts.isError && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    onClick: ()=>void contracts.refetch(),
                    children: "Retry loading contracts"
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note (optional)",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "aria-label": "Note",
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        disabled: addProof.isPending
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: error
                })
            ]
        })
    });
}
